<template>
	<section
		v-editable="blok"
		class="container overflow-hidden"
		:aria-label="blok.title ?? 'Seznam článků'"
	>
		<h2 v-if="blok.title" class="typo-h1 mb-6 md:mb-8">
			{{ blok.title }}
		</h2>
		<div v-if="pending" class="text-center py-5">
			<NuxtIcon
				name="custom/loader"
				class="text-icon-primary p-2 animate-spin text-2xl md:text-3xl lg:text-4xl inline-block"
				fill
			/>
		</div>
		<CarouselSkeleton
			v-else-if="blok.source.checkedLinks.length && articles && articles.length"
			:items="articles"
			list-class="inline-flex w-full min-w-max gap-4 md:min-w-full md:w-fit md:grid md:grid-cols-3"
		>
			<template #item="{item: article}">
				<li :key="article.id" class="rem:w-[180px] xs:rem:w-[300px] md:w-full">
					<ArticlePreviewTile :article="article" class="h-full" link-label="Zobrazit článek" />
				</li>
			</template>
		</CarouselSkeleton>
		<div v-else class="text-center my-16">
			<NuxtIcon name="frown" filled class="text-7xl mb-4 text-icon-primary text-center inline-block" />
			<h3 class="typo-h4 text-heading-primary mb-6">
				Bohužel nenašli jsme žádné články
			</h3>
			<span v-if="!blok.source.checkedLinks.length" class="text-functional-error-primary typo-body-s">Není vybraná složka článků.</span>
		</div>
		<div v-if="blok.cta_button?.length && !pending" class="flex justify-center mt-14 gap-2">
			<StoryblokComponent
				v-for="item in blok.cta_button"
				:key="item._uid"
				:blok="item"
				class="btn-primary btn-lg"
				:has-icon="true"
			/>
		</div>
	</section>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import type {ArticlePresentationListStoryblok} from '@/storyblok/types';
	import {useStoryblokHypermarketsStore} from '@/stores/storyblokHypermarkets';

	interface Props {
		blok: ArticlePresentationListStoryblok
		maxItems?: number
	}

	const props = defineProps<Props>();

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance} = storeToRefs(hypermarketsStore);

	const {data: articles, pending} = await useSingleStoryFetch(
		`article_list_${props.blok._uid}`,
		{
			by_slugs: getStoriesLoadPaths(props.blok.source.checkedLinks),
			is_startpage: false,
			per_page: 3,
			filter_query: {
				__or: [
					{hypermarket: {is: 'empty_array'}},
					{hypermarket: {all_in_array: selectedHypermarketInstance.value?.uuid}},
				],
				component: {like: 'PageType*Detail'},
				dateOfPublication: {lt_date: fetchTodayOrEarlierFilterDate()},
			},
			sort_by: 'content.dateOfPublication:desc',
		},
		{watch: [selectedHypermarket]},
	);
</script>
